"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.downloadUrl = downloadUrl;
exports.fetchData = fetchData;
exports.fetchDataIgnoringTokenUsage = fetchDataIgnoringTokenUsage;
exports.fetchUrl = fetchUrl;
var _sweetalert = _interopRequireDefault(require("sweetalert2"));
var _HttpStatus = require("../../constants/HttpStatus");
var _DmsTokenHook = require("../DmsTokenHook/DmsTokenHook");
var _server = require("../../service/server/server");
var _notifications = require("../../service/notifications/notifications");
var _reactIntlUniversal = _interopRequireDefault(require("react-intl-universal"));
var _FetchType = require("../../constants/FetchType");
var _userInteractionToken = require("../../functions/userInteractionToken");
var _ExportFormats = require("../../constants/ExportFormats");
var _FetchErrorMessages = require("../../constants/FetchErrorMessages");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Downloads a file from the given URL.
 *
 * @async
 * @function downloadUrl
 * @param {string} url - The backend URL to fetch the file from.
 * @param {string} [token=null] - User's authentication token.
 * @returns {Promise<void>}
 *
 * @example
 * downloadUrl('/path/to/file');
 */
async function downloadUrl(url) {
  let token = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  const authHeader = () => {
    if (token) {
      token = (0, _DmsTokenHook.getToken)();
      if (!token) {
        return {};
      }
      return {
        Authorization: "Bearer ".concat(token.access_token)
      };
    }
    return {};
  };
  const headerParams = {
    method: "GET",
    headers: authHeader()
  };
  const url_ = (0, _server.getURL)(url);
  let file_name = null;
  const notification = _notifications.Notifications.toast({
    title: _reactIntlUniversal.default.get("PreparingDownload").d("Preparing download..."),
    loading: true
  });
  await fetch(url_, headerParams).then(response => {
    try {
      const disposition = response.headers.get("content-disposition");
      if (disposition) {
        var _disposition$split$;
        file_name = ((_disposition$split$ = disposition.split("filename=")[1]) === null || _disposition$split$ === void 0 ? void 0 : _disposition$split$.replace(/['"]+/g, "")) || null;
      }
    } catch (e) {
      console.error("Fetch error:", e);
    }
    if (response.status === _HttpStatus.HTTP_STATUS.OK_200) return response.blob();
  }).then(blob => {
    if (!blob) return;
    const blob_url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blob_url;
    if (file_name) link.setAttribute("download", file_name);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => _notifications.Notifications.updateToast(notification, {
      title: _reactIntlUniversal.default.get("Downloading").d("Downloading!"),
      type: _notifications.Notifications.TYPE_SUCCESS,
      loading: false
    }), 100);
  }).catch(e => {
    console.log(e);
    setTimeout(() => _notifications.Notifications.updateToast(notification, {
      title: _reactIntlUniversal.default.get("FailedToPrepareDownload").d("Failed to prepare download!"),
      type: _notifications.Notifications.TYPE_ERROR,
      loading: false
    }), 100);
  });
}

/**
 * Fetches data from the given URL with the specified method and options.
 *
 * @async
 * @function fetchUrl
 * @param {string} url - The backend URL to fetch data from.
 * @param {string} method - The HTTP method to use (GET, POST, PUT, DELETE).
 * @param {string} [token=null] - User's authentication token.
 * @param {Object} [data=null] - The data to send with the request.
 * @param {boolean} [handle_failures=false] - Whether to handle failures internally.
 * @param {Function} [handle_swal=null] - Optional function to handle SweetAlert.
 * @param {string} [ipv4=null] - Optional IPv4 address for the request.
 * @param {number} [port=null] - Optional port for the request.
 * @param {string} [content_type=FETCH_TYPE.JSON_FORMAT] - Content type of the request.
 * @param {boolean} [put_form_data=true] - Whether to put form data in the request body.
 * @param {string} [accept=FETCH_TYPE.JSON_FORMAT] - Expected response format.
 * @returns {Promise<Object>} The response data and status.
 *
 * @example
 * const response = await fetchUrl('/api/data', 'GET');
 */
async function fetchUrl(url, method) {
  let token = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  let data = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  let handle_failures = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  let handle_swal = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : null;
  let ipv4 = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : null;
  let port = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : null;
  let content_type = arguments.length > 8 && arguments[8] !== undefined ? arguments[8] : _FetchType.FetchType.JSON_FORMAT;
  let put_form_data = arguments.length > 9 && arguments[9] !== undefined ? arguments[9] : true;
  let accept = arguments.length > 10 && arguments[10] !== undefined ? arguments[10] : _FetchType.FetchType.JSON_FORMAT;
  const authHeader = () => {
    if (token) {
      token = (0, _DmsTokenHook.getToken)();
      if (!token) {
        return {};
      }
      return {
        Authorization: "Bearer ".concat(token.access_token)
      };
    }
    return {};
  };
  const headerParams = {
    method: method,
    headers: _objectSpread(_objectSpread({}, authHeader()), {}, {
      Accept: accept
    })
  };
  const getApiMethodMessage = method => {
    const messages = {
      POST: _reactIntlUniversal.default.get("Created").d("Created"),
      PUT: _reactIntlUniversal.default.get("Updated").d("Updated"),
      DELETE: _reactIntlUniversal.default.get("Deleted").d("Deleted")
    };
    return messages[method] || method;
  };
  if (data) {
    if (content_type === _FetchType.FetchType.MULTIPART_FORMAT) {
      if (put_form_data) {
        let formData = new FormData();
        formData.append("file", data);
        headerParams.body = formData;
      } else {
        headerParams.body = data;
      }
    } else {
      headerParams.body = JSON.stringify(data);
      headerParams.headers["Content-Type"] = content_type;
    }
  }
  const url_ = (0, _server.getURL)(url, ipv4, port);
  try {
    if (accept === _FetchType.FetchType.OCTET_STREAM_FORMAT) {
      const response = await fetch(url_, headerParams);
      if (!response.ok) {
        throw new Error("HTTP error! Status: ".concat(response.status));
      }
      const FILENAME_REGEX = /filename[^;=\n]*=["']?([^"';\n]+)["']?/;
      const contentDisposition = response.headers.get("content-disposition");
      let fileName = "".concat(_ExportFormats.FILE_PREFIX.DOWNLOAD, ".").concat(content_type.split("/")[1] || _ExportFormats.EXPORT_FORMATS.BIN);
      if (contentDisposition) {
        const matches = contentDisposition.match(FILENAME_REGEX);
        if (matches && matches.length > 1) {
          fileName = matches[1];
        }
      }
      const fileBlob = await response.blob();
      const fileUrl = window.URL.createObjectURL(fileBlob);
      const downloadLink = document.createElement("a");
      downloadLink.href = fileUrl;
      downloadLink.setAttribute("download", fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(fileUrl);
      return {
        file: fileBlob,
        fileName,
        status: response.status
      };
    } else if (content_type === _FetchType.FetchType.JSON_FORMAT || content_type === _FetchType.FetchType.MULTIPART_FORMAT) {
      return await fetch(url_, headerParams).then(response => response.json().then(data => ({
        data: data,
        status: response.status
      })).then(res => {
        if (res.status === _HttpStatus.HTTP_STATUS.UNAUTHORIZED_401) {
          sessionStorage.clear();
          (0, _DmsTokenHook.credentialsFlush)();
          window.location.href = "/?message=".concat(res.data.detail);
          return res;
        }

        // Handle 403 Forbidden - user is authenticated but lacks permission
        // Do NOT clear session or logout - just dispatch event for UI handling
        if (res.status === _HttpStatus.HTTP_STATUS.FORBIDDEN_403) {
          window.dispatchEvent(new CustomEvent('accessDenied', {
            detail: {
              message: res.data.detail
            }
          }));
          return res;
        }
        if (token) {
          (0, _userInteractionToken.userInteractionToken)();
        }
        if (handle_swal) {
          if (res.status >= _HttpStatus.HTTP_STATUS.OK_200 && res.status < _HttpStatus.HTTP_STATUS.MULTIPLE_CHOICES_300) {
            _sweetalert.default.fire({
              icon: "success",
              title: getApiMethodMessage(method),
              position: "top-end",
              toast: true,
              showConfirmButton: false,
              timer: 3000,
              width: "20rem",
              padding: "1rem",
              height: "5rem",
              timerProgressBar: true,
              allowOutsideClick: false
            });
          } else {
            if (Array.isArray(res.data.detail)) {
              _sweetalert.default.fire({
                icon: "error",
                timer: 3000,
                title: res.data.detail.message
              });
            } else {
              _sweetalert.default.fire({
                icon: "error",
                timer: 3000,
                title: res.data.detail
              });
            }
          }
        }
        return res;
      }));
    } else {
      return await fetch(url_, headerParams).then(async response => {
        if (!response.ok) {
          throw new Error("HTTP error! Status: ".concat(response.status));
        }
        const blob = await response.blob();
        if (response.status === _HttpStatus.HTTP_STATUS.UNAUTHORIZED_401) {
          sessionStorage.clear();
          window.location.href = "/?message=".concat(response.data.detail);
          return response;
        }
        if (token) {
          (0, _userInteractionToken.userInteractionToken)();
        }
        return {
          data: blob
        };
      }).catch(error => {
        console.error("Error fetching image:", error);
        throw error;
      });
    }
  } catch (ex) {
    if (!handle_failures) {
      if (ex.message === _FetchErrorMessages.FETCH_ERROR_MESSAGES.FAILED_TO_FETCH || ex.message === _FetchErrorMessages.FETCH_ERROR_MESSAGES.NETWORK_ERROR) {
        TitleError = _reactIntlUniversal.default.get("CommunicationFailed").d("Communication failed");
        text = _reactIntlUniversal.default.get("VerifyConnection").d("Verify server connection");
        _notifications.Notifications.add("CommunicationFailed_" + Date.now(), TitleError, text);
      }
      _sweetalert.default.fire({
        icon: "error",
        title: ex.message
      });
      throw ex;
    }
    return {
      data: {
        detail: ex.message
      },
      status: _HttpStatus.HTTP_STATUS.INTERNAL_SERVER_ERROR_500
    };
  }
}

/**
 * Fetches data from the given URL with the specified method and options.
 * This function does not include the status in the response.
 *
 * @async
 * @function fetchData
 * @param {string} url - The backend URL to fetch data from.
 * @param {string} method - The HTTP method to use (GET, POST, PUT, DELETE).
 * @param {string} [token=false] - User's authentication token.
 * @param {Object} [data=null] - The data to send with the request.
 * @param {boolean} [handle_failures=false] - Whether to handle failures internally.
 * @param {Function} [handle_swal=null] - Optional function to handle SweetAlert.
 * @param {string} [ipv4=null] - Optional IPv4 address for the request.
 * @param {number} [port=null] - Optional port for the request.
 * @param {string} [content_type=FETCH_TYPE.JSON_FORMAT] - Content type of the request.
 * @param {boolean} [put_form_data=true] - Whether to put form data in the request body.
 * @param {string} [accept=FETCH_TYPE.JSON_FORMAT] - Expected response format.
 * @returns {Promise<Object>} The response data.
 *
 * @example
 * const response = await fetchData('/api/data', 'GET');
 */
async function fetchData(url, method) {
  let token = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  let data = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  let handle_failures = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  let handle_swal = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : null;
  let ipv4 = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : null;
  let port = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : null;
  let content_type = arguments.length > 8 && arguments[8] !== undefined ? arguments[8] : _FetchType.FetchType.JSON_FORMAT;
  let put_form_data = arguments.length > 9 && arguments[9] !== undefined ? arguments[9] : true;
  let accept = arguments.length > 10 && arguments[10] !== undefined ? arguments[10] : _FetchType.FetchType.JSON_FORMAT;
  const res = await fetchUrl(url, method, token, data, handle_failures, handle_swal, ipv4, port, content_type, put_form_data, accept);
  if (res.status === _HttpStatus.HTTP_STATUS.UNAUTHORIZED_401) {
    sessionStorage.clear();
    window.location.href = "/?message=".concat(res.data.detail);
  }
  return res;
}

/**
 * Fetches data from the given URL without incrementing the token usage.
 *
 * @async
 * @function fetchDataIgnoringTokenUsage
 * @param {...any} args - The same arguments as fetchUrl.
 * @returns {Promise<Object>} The response data.
 *
 * @example
 * const response = await fetchDataIgnoringTokenUsage('/api/data', 'GET');
 */
async function fetchDataIgnoringTokenUsage() {
  let prevRefreshTokenData = sessionStorage.getItem("refresh");
  const res = await fetchUrl(...arguments);
  sessionStorage.setItem("refresh", prevRefreshTokenData);
  return res;
}